import { buildRichInlineOverlay, type RichOverlayToken } from './diffUtils'
import { preprocessLedgerText, type WitnessUnitTextRecord } from './ledgerTextPreprocessing'
import type { Witness } from '../store/booksStore'
import { getRowLabel, type AnchoredRow, type UnitMapping } from '../types/anchoredRowTypes'

export interface LedgerSafeTraditionFolioMarker {
  marker: string
}

export interface LedgerSafeTraditionUnit {
  rowId: string
  rowLabel: string
  index: number
  text: string
  comparisonText: string
  unitLabel?: string
  sourceUnitUuid: string
  boundaryKind: 'ledger-row'
  peerCount: number
  hasPeers: boolean
  folioMarkersBefore?: LedgerSafeTraditionFolioMarker[]
}

export interface LedgerSafeTraditionPeerUnit {
  rowId: string
  rowLabel: string
  witnessId: string
  witnessLabel: string
  traditionLabel: string
  unitLabel?: string
  text: string
  comparisonText: string
  status?: UnitMapping['translationStatus']
  diff: {
    tokens: RichOverlayToken[]
    stats: {
      total: number
      changed: number
      inserts: number
      deletes: number
      substitutes: number
    }
  }
}

export type LedgerSafeTraditionWitness = Partial<Witness> & Pick<Witness, 'id' | 'sigla'>

export interface LedgerSafeTraditionReaderModel {
  sourceWitness: LedgerSafeTraditionWitness | null
  units: LedgerSafeTraditionUnit[]
  apparatusByRowId: Record<string, LedgerSafeTraditionPeerUnit[]>
}

interface BuildLedgerSafeTraditionReaderModelInput {
  rows: AnchoredRow[]
  witnesses: LedgerSafeTraditionWitness[]
  sourceWitnessId: string
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>
}

const FOLIO_MARKER_PATTERN = /\bfol(?:io)?\.?\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*[rv]\.?/gi

// Peer diffs are pure functions of the two comparison texts, which are stable
// across model rebuilds. Caching them turns every rebuild after the first
// (witness churn, selector churn, scope switches) into cache hits instead of
// re-running the O(m*n) edit-script DP for every peer of every row.
const MAX_PEER_DIFF_CACHE_ENTRIES = 8000
const peerDiffCache = new Map<string, LedgerSafeTraditionPeerUnit['diff']>()

const getCachedPeerDiff = (
  sourceComparisonText: string,
  peerComparisonText: string
): LedgerSafeTraditionPeerUnit['diff'] => {
  const cacheKey = `${sourceComparisonText}\u0000${peerComparisonText}`
  const cached = peerDiffCache.get(cacheKey)
  if (cached) {
    return cached
  }
  const diff = buildRichInlineOverlay(sourceComparisonText, peerComparisonText)
  peerDiffCache.set(cacheKey, diff)
  if (peerDiffCache.size > MAX_PEER_DIFF_CACHE_ENTRIES) {
    const oldestKey = peerDiffCache.keys().next().value
    if (typeof oldestKey === 'string') {
      peerDiffCache.delete(oldestKey)
    }
  }
  return diff
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => (
  [...rows].sort((left, right) => {
    if (left.position !== right.position) {
      return left.position - right.position
    }
    if (left.labelNumber !== right.labelNumber) {
      return left.labelNumber - right.labelNumber
    }
    return left.id.localeCompare(right.id)
  })
)

const getActiveMapping = (row: AnchoredRow, witnessId: string): UnitMapping | null => (
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded) || null
)

const getTraditionLabel = (witness: Partial<Witness> & Pick<Witness, 'id' | 'sigla'>): string => (
  witness.description?.trim()
  || witness.library?.trim()
  || witness.sigla
)

const extractFolioMarkers = (rawText: string): LedgerSafeTraditionFolioMarker[] => {
  const markers = Array.from(rawText.matchAll(FOLIO_MARKER_PATTERN))
    .map(match => match[0]?.trim())
    .filter((marker): marker is string => Boolean(marker))
  return Array.from(new Set(markers)).map(marker => ({ marker }))
}

const resolveMappingText = (
  mapping: UnitMapping,
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>,
): WitnessUnitTextRecord | null => {
  const canonical = mapping.unitUuid
    ? textLookupByWitnessId?.get(mapping.witnessId)?.get(mapping.unitUuid)
    : null
  if (canonical) {
    return canonical
  }

  const fallbackText = mapping.textSnapshot || ''
  if (!fallbackText.trim()) {
    return null
  }
  const preprocessed = preprocessLedgerText(fallbackText)
  return {
    rawText: fallbackText,
    cleanSourceText: preprocessed.cleanSourceText,
    workspaceDisplayText: preprocessed.workspaceDisplayText,
    readingText: preprocessed.readingText,
    comparisonText: preprocessed.comparisonText,
  }
}

export const buildLedgerSafeTraditionReaderModel = ({
  rows,
  sourceWitnessId,
  textLookupByWitnessId,
  witnesses,
}: BuildLedgerSafeTraditionReaderModelInput): LedgerSafeTraditionReaderModel => {
  const witnessOrder = new Map(witnesses.map((witness, index) => [witness.id, index]))
  const witnessById = new Map(witnesses.map(witness => [witness.id, witness]))
  const sourceWitness = witnessById.get(sourceWitnessId) || null
  const units: LedgerSafeTraditionUnit[] = []
  const apparatusByRowId: Record<string, LedgerSafeTraditionPeerUnit[]> = {}

  sortRows(rows).forEach(row => {
    const sourceMapping = getActiveMapping(row, sourceWitnessId)
    if (!sourceMapping) {
      return
    }

    const sourceTextRecord = resolveMappingText(sourceMapping, textLookupByWitnessId)
    const sourceText = sourceTextRecord?.workspaceDisplayText || ''
    if (!sourceText.trim()) {
      return
    }

    const rowLabel = getRowLabel(row)
    const sourceComparisonText = sourceTextRecord?.comparisonText || sourceText
    const peers = row.unitMappings
      .filter(mapping => mapping.witnessId !== sourceWitnessId && !mapping.excluded)
      .map((mapping): LedgerSafeTraditionPeerUnit | null => {
        const witness = witnessById.get(mapping.witnessId)
        const peerTextRecord = resolveMappingText(mapping, textLookupByWitnessId)
        const peerText = peerTextRecord?.workspaceDisplayText || ''
        if (!witness || !peerText.trim()) {
          return null
        }
        const peerComparisonText = peerTextRecord?.comparisonText || peerText
        const diff = getCachedPeerDiff(sourceComparisonText, peerComparisonText)
        return {
          rowId: row.id,
          rowLabel,
          witnessId: mapping.witnessId,
          witnessLabel: witness.sigla,
          traditionLabel: getTraditionLabel(witness),
          unitLabel: mapping.unitLabel,
          text: peerText,
          comparisonText: peerComparisonText,
          status: mapping.translationStatus,
          diff,
        }
      })
      .filter((peer): peer is LedgerSafeTraditionPeerUnit => Boolean(peer))
      .sort((left, right) => {
        const leftOrder = witnessOrder.get(left.witnessId) ?? Number.MAX_SAFE_INTEGER
        const rightOrder = witnessOrder.get(right.witnessId) ?? Number.MAX_SAFE_INTEGER
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder
        }
        return left.witnessLabel.localeCompare(right.witnessLabel)
      })

    apparatusByRowId[row.id] = peers
    units.push({
      rowId: row.id,
      rowLabel,
      index: units.length,
      text: sourceText,
      comparisonText: sourceComparisonText,
      unitLabel: sourceMapping.unitLabel,
      sourceUnitUuid: sourceMapping.unitUuid,
      boundaryKind: 'ledger-row',
      peerCount: peers.length,
      hasPeers: peers.length > 0,
      folioMarkersBefore: extractFolioMarkers(sourceTextRecord?.rawText || sourceText),
    })
  })

  return {
    sourceWitness,
    units,
    apparatusByRowId,
  }
}
